import type { AuthProvider } from "ra-core";

import { clearPersistedQueryCache } from "../../root/persistedQueryCache";
import {
  clearSession,
  completeLogin,
  consumeAuthCallback,
  getValidSession,
  hostedLoginUrl,
  hostedLogoutUrl,
  isCognitoConfigured,
  loginInProgress,
  readSession,
  revokeRefreshToken,
} from "./cognito";
import { fetchMe, type Me } from "./dataProvider";
import {
  getStubCustomerId,
  setStubCustomerId,
  setStubRole,
  STUB_ROLES,
} from "./principal";

/** A 403 for a write the role may not make is not a lost session (W9 F4). */
const isForbidden = (error: { body?: { error?: string } }) =>
  error?.body?.error === "forbidden" ||
  error?.body?.error === "workspace_forbidden";

// One /me per signed-in user; cleared on logout.
let me: { sub: string; promise: Promise<Me> } | null = null;

const IDENTITY = {
  "100004": {
    id: "11111111-1111-1111-1111-111111111111",
    fullName: "Woodley LO",
  },
  "100081": {
    id: "22222222-2222-2222-2222-222222222222",
    fullName: "Envoy LO",
  },
} as const;

export const getAuthProvider = (): AuthProvider => {
  if (!isCognitoConfigured()) {
    // A production build without Cognito env would otherwise come up as the
    // stub, trusting a client-chosen tenant header. Fail loudly instead.
    if (
      import.meta.env.PROD &&
      import.meta.env.VITE_ALLOW_STUB_AUTH !== "true"
    ) {
      throw new Error(
        "Cognito is not configured for this production build (set VITE_COGNITO_*).",
      );
    }
    return {
      login: async (params: { customerId?: string; role?: string } = {}) => {
        const id = params.customerId === "100081" ? "100081" : "100004";
        setStubCustomerId(id);
        setStubRole(STUB_ROLES.find((role) => role === params.role) ?? "admin");
      },
      logout: async () => {
        clearPersistedQueryCache();
        setStubCustomerId("100004");
        setStubRole("admin");
      },
      checkAuth: async () => undefined,
      checkError: async () => undefined,
      getIdentity: async () => IDENTITY[getStubCustomerId()],
      // The local BFF answers for the stub role (x-ardley-role).
      getPermissions: async () => (await fetchMe()).permissions,
      // Reading is open to every member; writes are gated by getPermissions.
      canAccess: async () => true,
    };
  }

  return {
    login: async () => {
      window.location.assign(await hostedLoginUrl());
      return Promise.reject();
    },
    logout: async () => {
      const session = readSession();
      me = null;
      clearSession();
      clearPersistedQueryCache();
      // Only bounce through Cognito hosted logout after a real session.
      // checkAuth failures also call logout; sending those to /logout
      // redirects back here and loops. Returning the absolute URL (rather than
      // navigating ourselves) lets ra-core clear its store and query cache
      // before it redirects.
      if (session) {
        revokeRefreshToken(session);
        return hostedLogoutUrl();
      }
    },
    checkAuth: async () => {
      const callback = consumeAuthCallback();
      if (callback) {
        await completeLogin(callback);
        return;
      }
      const login = loginInProgress();
      if (login) {
        await login;
        return;
      }
      if (await getValidSession()) return;
      throw new Error("not_authenticated");
    },
    checkError: async (error: {
      status?: number;
      body?: { error?: string };
    }) => {
      if (isForbidden(error)) return;
      if (error?.status === 401 || error?.status === 403) {
        clearSession();
        throw error;
      }
    },
    // The id is crm_users.id from the BFF, the same id rows store as owner_id
    // (invariant 3). The Cognito sub is only how the BFF finds that user.
    getIdentity: async () => {
      const session = readSession();
      if (!session?.sub) throw new Error("not_authenticated");
      const identity = await currentMe(session.sub);
      return {
        id: identity.ownerId,
        fullName: identity.fullName || session.name || session.email || "",
      };
    },
    handleCallback: async () => {
      const callback = consumeAuthCallback();
      if (!callback) throw new Error("missing_code");
      await completeLogin(callback);
    },
    getPermissions: async () => {
      // Right after the hosted-UI redirect the session is still being
      // written; asking now would cache "no permissions" for the visit.
      await loginInProgress();
      const session = readSession() ?? (await getValidSession());
      if (!session?.sub) return [];
      return (await currentMe(session.sub)).permissions;
    },
    canAccess: async () => true,
  };
};

/** The signed-in user's /me, fetched once per session sub. */
function currentMe(sub: string): Promise<Me> {
  if (me?.sub !== sub) {
    const promise = fetchMe();
    me = { sub, promise };
    promise.catch(() => {
      if (me?.promise === promise) me = null;
    });
  }
  return me.promise;
}
